import { useState, useEffect, useId } from "react";
import { BRAND, GREEN, GREEN_TEXT, INK, text, oneLine, WHITE } from "./constants";
import Logo from "./Logo";

// ─── INSIGNIA DE CREDENCIAL (22/10) ──────────────────────────────────────────
// Medalla hexagonal de esquinas redondeadas: delante el puesto (role, sector,
// stage), detrás el dato (metric_value, metric_label), y el logo arriba en las
// dos caras. Animación mínima, solo transform y opacity:
//
// - Quieta, enseñando la cara de delante. Sin giro continuo.
// - Con el puntero encima se inclina siguiéndolo.
// - Click, Enter o espacio: le da la vuelta. Para editar, el botón "Editar"
//   de la tarjeta.
// - Un destello cruza la cara de vez en cuando.
// - Con prefers-reduced-motion no se inclina, la vuelta es instantánea y no
//   hay destello.
//
// Volumen: las dos caras van separadas DEPTH px en Z y el canto es una banda
// continua de tiras que siguen el contorno (como un cilindro en CSS), cada una
// sombreada según hacia dónde mira y con un degradado a lo ancho que la hace
// parecer redondeada. El acento colorea el borde, el canto y el logo; el
// texto va siempre en INK.

export const ACCENTS = { ink: INK, blue: BRAND, green: GREEN };
// Logo en el color del acento, salvo el verde: como texto es ilegible
const LOGO_COLOR = { ink: INK, blue: BRAND, green: GREEN_TEXT };
const W = 200, H = 231; // hexágono con punta arriba: alto = ancho · 2/√3
const CX = W / 2, CY = H / 2;
const DEPTH = 12;       // grosor de la medalla
const SHINE_S = 9;      // cada cuánto pasa el destello

// Hexágono con las esquinas redondeadas (radio R a lo largo de cada lado)
const R = 24;
const CORNER_STEPS = 8; // tramos del canto en cada esquina
const VERTS = [[100, 0], [200, 57.7], [200, 173.2], [100, 231], [0, 173.2], [0, 57.7]];
const along = (a, b, d) => {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  return [a[0] + ((b[0] - a[0]) * d) / len, a[1] + ((b[1] - a[1]) * d) / len];
};
// Esquina i: empieza en a (hacia el vértice anterior), control en el vértice, acaba en b
const corners = VERTS.map((v, i) => {
  const n = VERTS.length;
  return { a: along(v, VERTS[(i + n - 1) % n], R), v, b: along(v, VERTS[(i + 1) % n], R) };
});
const f1 = (p) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`;
const HEX_PATH = corners.map((c, i) => `${i === 0 ? "M" : "L"}${f1(c.a)} Q${f1(c.v)} ${f1(c.b)}`).join(" ") + " Z";
const CLIP = `path("${HEX_PATH}")`;
const shrink = (k) => `translate(${CX} ${CY}) scale(${k}) translate(${-CX} ${-CY})`;

// Contorno muestreado (los mismos puntos de la curva) para el canto
const OUTLINE = corners.flatMap(({ a, v, b }) =>
  Array.from({ length: CORNER_STEPS + 1 }, (_, k) => {
    const t = k / CORNER_STEPS, u = 1 - t;
    return [u * u * a[0] + 2 * u * t * v[0] + t * t * b[0], u * u * a[1] + 2 * u * t * v[1] + t * t * b[1]];
  }));
// Una tira por tramo: posición, ángulo, largo y cuánta luz recibe (luz arriba a la izquierda)
const STRIPS = OUTLINE.map((p, i) => {
  const q = OUTLINE[(i + 1) % OUTLINE.length];
  const dx = q[0] - p[0], dy = q[1] - p[1];
  const len = Math.hypot(dx, dy);
  if (len < 0.01) return null;
  const nx = dy / len, ny = -dx / len;           // normal hacia fuera (contorno en sentido horario)
  return { x: p[0], y: p[1], len, angle: (Math.atan2(dy, dx) * 180) / Math.PI, light: nx * -0.6 + ny * -0.8 };
}).filter(Boolean);

// Mezcla un color #rrggbb con negro (f < 0) o blanco (f > 0)
const tint = (hex, f) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c => Math.round(f < 0 ? c * (1 + f) : c + (255 - c) * f));
  return `rgb(${ch.join(",")})`;
};
const expand = (hex) => (hex.length === 4 ? "#" + hex.slice(1).split("").map(c => c + c).join("") : hex);

const CSS = `
@keyframes rtm-cred-shine { 0% { transform: translateX(-120%) skewX(-20deg); opacity: 0; } 3% { opacity: 1; } 12%, 100% { transform: translateX(220%) skewX(-20deg); opacity: 0; } }
.rtm-cred:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 6px; border-radius: 12px; }
`;

const useReducedMotion = () => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
};

const Face = ({ back, accent, gid, shine, logoColor, children }) => (
  <div style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: `${back ? "rotateY(180deg) " : ""}translateZ(${DEPTH / 2}px)`, clipPath: CLIP, WebkitClipPath: CLIP, overflow: "hidden" }}>
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ position: "absolute", inset: 0 }} aria-hidden="true">
      <defs>
        {/* Bisel del borde: luz arriba a la izquierda, sombra abajo a la derecha */}
        <linearGradient id={`${gid}-rim`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={tint(accent, 0.55)} />
          <stop offset="0.45" stopColor={accent} />
          <stop offset="1" stopColor={tint(accent, -0.45)} />
        </linearGradient>
        <linearGradient id={`${gid}-bevel`} x1="1" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor={tint(accent, 0.35)} />
          <stop offset="1" stopColor={tint(accent, -0.3)} />
        </linearGradient>
        <radialGradient id={`${gid}-glow`} cx="0.32" cy="0.22" r="0.95">
          <stop offset="0" stopColor={WHITE} />
          <stop offset="0.6" stopColor={tint(accent, 0.94)} />
          <stop offset="1" stopColor={tint(accent, 0.82)} />
        </radialGradient>
      </defs>
      <path d={HEX_PATH} fill={`url(#${gid}-rim)`} />
      <path d={HEX_PATH} transform={shrink(0.94)} fill={`url(#${gid}-bevel)`} />
      <path d={HEX_PATH} transform={shrink(0.86)} fill={`url(#${gid}-glow)`} />
      {/* Hundido del campo: sombra arriba, luz abajo */}
      <path d={HEX_PATH} transform={`translate(0.8 1.2) ${shrink(0.86)}`} fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="2" />
      <path d={HEX_PATH} transform={`translate(-0.6 -0.8) ${shrink(0.86)}`} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1" />
      <path d={HEX_PATH} transform={shrink(0.77)} fill="none" stroke={accent} strokeOpacity="0.25" strokeWidth="1" />
    </svg>
    <div style={{ position: "absolute", inset: "0 30px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 6 }}>
      <Logo size={11} color={logoColor} style={{ marginBottom: 8, opacity: 0.85 }} />
      {children}
    </div>
    {shine && <div aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "45%", background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0) 100%)", pointerEvents: "none", opacity: 0, ...shine }} />}
  </div>
);

export default function CredentialBadge({ credential, lang = "en" }) {
  const es = lang === "es";
  const reduced = useReducedMotion();
  const gid = useId().replace(/:/g, "");
  const [turns, setTurns] = useState(0);    // medias vueltas dadas (siempre en el mismo sentido)
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const c = credential || {};
  const accent = expand(ACCENTS[c.accent] || INK);
  const logoColor = LOGO_COLOR[c.accent] || INK;
  const label = [c.role, c.sector, c.stage].filter(Boolean).join(", ")
    + (c.metric_value || c.metric_label ? `. ${[c.metric_value, c.metric_label].filter(Boolean).join(" ")}` : "");

  const move = (e) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: -py * 16, y: px * 20 });
  };
  const flip = () => setTurns(t => t + 1);
  // El destello de la trasera va desfasado, para que no coincidan
  const shine = (offset) => reduced ? null : { animation: `rtm-cred-shine ${SHINE_S}s ease-in-out infinite`, animationDelay: `${offset}s` };

  return (
    <div role="button" tabIndex={0} className="rtm-cred" aria-label={label}
      aria-pressed={turns % 2 === 1}
      title={es ? "Pulsa para darle la vuelta" : "Click to flip"}
      onClick={flip}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); flip(); } }}
      onPointerMove={move} onPointerLeave={() => setTilt({ x: 0, y: 0 })}
      style={{ position: "relative", width: W, height: H + 18, perspective: 900, cursor: "pointer", margin: "0 auto", flexShrink: 0 }}>
      <style>{CSS}</style>
      {/* Sombra en el suelo */}
      <div aria-hidden="true" style={{ position: "absolute", left: "18%", right: "18%", bottom: 0, height: 14, borderRadius: "50%", background: "radial-gradient(ellipse at center, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0) 70%)" }} />
      <div style={{ width: W, height: H, transformStyle: "preserve-3d", transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`, transition: "transform 0.2s ease-out" }}>
        <div style={{ position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d", transform: `rotateY(${turns * 180}deg)`, transition: reduced ? "none" : "transform 0.7s cubic-bezier(.3,.7,.3,1)" }}>
          {/* Canto: tiras unidas a lo largo del contorno, de cara a cara */}
          {STRIPS.map((s, i) => {
            const base = tint(accent, -0.15 + 0.4 * s.light);
            const dark = tint(accent, -0.55 + 0.2 * Math.max(0, s.light));
            return (
              <div key={i} aria-hidden="true" style={{
                position: "absolute", left: 0, top: 0, width: s.len + 0.8, height: DEPTH, transformOrigin: "0 0",
                transform: `translate(${s.x.toFixed(2)}px, ${s.y.toFixed(2)}px) rotateZ(${s.angle.toFixed(2)}deg) rotateX(90deg) translateY(${-DEPTH / 2}px)`,
                background: `linear-gradient(to bottom, ${dark}, ${base} 50%, ${dark})`,
              }} />
            );
          })}
          <Face accent={accent} gid={`${gid}f`} shine={shine(0)} logoColor={logoColor}>
            <div style={{ ...text.h2, lineHeight: 1.25, overflowWrap: "anywhere" }}>{c.role || (es ? "Puesto" : "Role")}</div>
            <div style={{ ...text.cardSub, maxWidth: "100%", ...oneLine }}>{c.sector}</div>
            <div style={{ ...text.cardSub, maxWidth: "100%", ...oneLine }}>{c.stage}</div>
          </Face>
          <Face back accent={accent} gid={`${gid}b`} shine={shine(SHINE_S / 2)} logoColor={logoColor}>
            <div style={{ ...text.stat, overflowWrap: "anywhere" }}>{c.metric_value || "—"}</div>
            <div style={{ ...text.cardSub, lineHeight: 1.4 }}>{c.metric_label}</div>
          </Face>
        </div>
      </div>
    </div>
  );
}
